import { useCallback, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useSearchableLimitedOptions } from 'components/ui/hooks';
import { OverflowTooltip } from 'components/ui/OverflowTooltip';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
} from 'components/ui/PrimaryNavigation';
import { isKeyboardActivationKey } from 'components/ui/utilities/keyboardNavigation';
import type {
  DialogSearchableMultiSelectProps,
  DialogSearchableMultiSelectValue,
} from './DialogSearchableMultiSelect.props';
import {
  DialogSearchableMultiSelectAddButton,
  DialogSearchableMultiSelectCheckbox,
  DialogSearchableMultiSelectChevron,
  DialogSearchableMultiSelectChip,
  DialogSearchableMultiSelectChipLabel,
  DialogSearchableMultiSelectChipRemoveButton,
  DialogSearchableMultiSelectClearButton,
  DialogSearchableMultiSelectField,
  DialogSearchableMultiSelectPlaceholder,
  DialogSearchableMultiSelectRoot,
  DialogSearchableMultiSelectValues,
} from './DialogSearchableMultiSelect.styles';

const DEFAULT_VISIBLE_LIMIT = 10;
const DEFAULT_MENU_MAX_WIDTH = 320;
const DEFAULT_MENU_MIN_WIDTH = 240;

const getSelectedOptions = ({
  optionsById,
  selectedIds,
}: {
  optionsById: Map<DialogSearchableMultiSelectValue, string>;
  selectedIds: readonly DialogSearchableMultiSelectValue[];
}) => selectedIds
  .map(id => {
    const label = optionsById.get(id);

    return label ? { id, label } : null;
  })
  .filter((option): option is { id: DialogSearchableMultiSelectValue; label: string } => Boolean(option));

const getMultiSelectSummary = (selectedOptionCount: number, placeholder: string) => {
  if (selectedOptionCount === 0) {
    return placeholder;
  }

  return `${selectedOptionCount} selected`;
};

const DialogSearchableMultiSelectChipItem = ({
  chipTone,
  disabled,
  label,
  onRemove,
  testId,
}: {
  chipTone: 'field' | 'list';
  disabled: boolean;
  label: string;
  onRemove: (event: MouseEvent<HTMLButtonElement>) => void;
  testId?: string;
}) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);

  return (
    <DialogSearchableMultiSelectChip chipTone={chipTone} data-testid={testId}>
      <OverflowTooltip measureRef={labelRef} placement="bottom" title={label}>
        <DialogSearchableMultiSelectChipLabel ref={labelRef}>
          {label}
        </DialogSearchableMultiSelectChipLabel>
      </OverflowTooltip>
      <DialogSearchableMultiSelectChipRemoveButton
        aria-label={`Remove ${label}`}
        disabled={disabled}
        onClick={onRemove}
        type="button">
        <CloseRoundedIcon fontSize="inherit" />
      </DialogSearchableMultiSelectChipRemoveButton>
    </DialogSearchableMultiSelectChip>
  );
};

const DialogSearchableMultiSelect = ({
  addButtonLabel = 'Add',
  clearSearchAriaLabel,
  clearSelectedAriaLabel = 'Clear selected options',
  clearSelectedTestId,
  disabled = false,
  emptyLabel,
  isLoading = false,
  itemLabelPlural,
  itemLabelSingular,
  label,
  matchTriggerWidth,
  menuTestId,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  searchTestId,
  selectTestId,
  selectedIds,
  selectedOptionTestId,
  selectedPlacement = 'field',
  showSearch = true,
  testIdForOption,
  triggerVariant = 'select',
  visibleLimit = DEFAULT_VISIBLE_LIMIT,
}: DialogSearchableMultiSelectProps) => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const optionsById = useMemo(
    () => new Map(options.map(option => [option.id, option.label])),
    [options]
  );
  const selectedOptions = useMemo(
    () => getSelectedOptions({ optionsById, selectedIds }),
    [optionsById, selectedIds]
  );
  const selectedSummary = getMultiSelectSummary(selectedOptions.length, placeholder);
  const {
    canShowFewerOptions,
    filteredOptions,
    hasMoreOptions,
    hiddenOptionCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions,
  } = useSearchableLimitedOptions({
    getSearchText: option => option.searchText ?? option.label,
    options,
    visibleLimit,
  });
  const isMenuOpen = Boolean(anchorEl);
  const isSelectDisabled = disabled || isLoading;
  const shouldMatchTriggerWidth = matchTriggerWidth ?? triggerVariant === 'select';
  const matchedTriggerWidth = shouldMatchTriggerWidth && anchorEl
    ? Math.round(anchorEl.getBoundingClientRect().width)
    : undefined;

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    resetSearchableOptions();
  }, [resetSearchableOptions]);

  const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
    if (!isSelectDisabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSelectKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!isSelectDisabled && isKeyboardActivationKey(event.key)) {
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    }
  };

  const handleToggleOption = (id: DialogSearchableMultiSelectValue) => {
    onChange(selectedIdSet.has(id)
      ? selectedIds.filter(selectedId => selectedId !== id)
      : [...selectedIds, id]);
  };

  const handleRemoveOption = (
    id: DialogSearchableMultiSelectValue,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    onChange(selectedIds.filter(selectedId => selectedId !== id));
  };

  const handleClearSelected = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onChange([]);
  };

  const selectedChips = selectedOptions.map(option => (
    <DialogSearchableMultiSelectChipItem
      key={option.id}
      chipTone={selectedPlacement}
      disabled={isSelectDisabled}
      label={option.label}
      onRemove={event => handleRemoveOption(option.id, event)}
      testId={selectedOptionTestId?.(option.id)}
    />
  ));

  return (
    <DialogSearchableMultiSelectRoot>
      {label}
      {triggerVariant === 'select' ? (
        <DialogSearchableMultiSelectField
          aria-disabled={isSelectDisabled ? 'true' : undefined}
          aria-expanded={isMenuOpen || undefined}
          aria-haspopup="listbox"
          data-testid={selectTestId}
          onClick={handleSelectClick}
          onKeyDown={handleSelectKeyDown}
          role="combobox"
          tabIndex={isSelectDisabled ? -1 : 0}>
          {selectedPlacement === 'field' && selectedOptions.length > 0 ? (
            <DialogSearchableMultiSelectValues>
              {selectedChips}
            </DialogSearchableMultiSelectValues>
          ) : (
            <DialogSearchableMultiSelectPlaceholder>
              {selectedSummary}
            </DialogSearchableMultiSelectPlaceholder>
          )}
          {selectedOptions.length > 0 && !isSelectDisabled ? (
            <DialogSearchableMultiSelectClearButton
              aria-label={clearSelectedAriaLabel}
              data-testid={clearSelectedTestId}
              onClick={handleClearSelected}
              type="button">
              <CloseRoundedIcon fontSize="inherit" />
            </DialogSearchableMultiSelectClearButton>
          ) : null}
          <DialogSearchableMultiSelectChevron>
            <ArrowDropDownIcon fontSize="inherit" />
          </DialogSearchableMultiSelectChevron>
        </DialogSearchableMultiSelectField>
      ) : null}
      {selectedPlacement === 'list' && selectedOptions.length > 0 ? (
        <DialogSearchableMultiSelectValues>
          {selectedChips}
        </DialogSearchableMultiSelectValues>
      ) : null}
      {triggerVariant === 'addButton' ? (
        <DialogSearchableMultiSelectAddButton
          aria-expanded={isMenuOpen || undefined}
          aria-haspopup="listbox"
          data-testid={selectTestId}
          disabled={isSelectDisabled}
          onClick={handleSelectClick}
          component="button"
          type="button"
          visualVariant="compact">
          <AddRoundedIcon fontSize="inherit" />
          {addButtonLabel}
        </DialogSearchableMultiSelectAddButton>
      ) : null}
      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        data-testid={menuTestId}
        disableAutoFocusItem
        menuMaxWidth={matchedTriggerWidth ?? DEFAULT_MENU_MAX_WIDTH}
        menuMinWidth={matchedTriggerWidth ?? DEFAULT_MENU_MIN_WIDTH}
        onClose={handleMenuClose}
        open={isMenuOpen}
        scrollHintItemLabelPlural={itemLabelPlural}
        scrollHintItemLabelSingular={itemLabelSingular}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{
          disableListWrap: true,
        }}>
        {showSearch ? (
          <PrimaryNavigationMenuSearch
            clearAriaLabel={clearSearchAriaLabel}
            fullWidth
            inputRef={searchInputRef}
            onChange={event => setSearchValue(event.target.value)}
            onClear={() => setSearchValue('')}
            placeholder={searchPlaceholder}
            value={searchValue}
            inputProps={{
              'data-testid': searchTestId,
            }}
          />
        ) : null}
        {visibleOptions.map(option => {
          const isSelected = selectedIdSet.has(option.id);

          return (
            <PrimaryNavigationMenuItem
              key={option.id}
              aria-selected={isSelected}
              data-testid={testIdForOption?.(option.id)}
              leadingSlot={(
                <DialogSearchableMultiSelectCheckbox
                  checked={isSelected}
                  readOnly
                  visualVariant="selection"
                />
              )}
              onClick={() => handleToggleOption(option.id)}
              role="option">
              {option.label}
            </PrimaryNavigationMenuItem>
          );
        })}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenOptionCount}
            itemLabelPlural={itemLabelPlural}
            itemLabelSingular={itemLabelSingular}
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural={itemLabelPlural}
            itemLabelSingular={itemLabelSingular}
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {filteredOptions.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            {emptyLabel}
          </PrimaryNavigationMenuItem>
        )}
      </PrimaryNavigationMenu>
    </DialogSearchableMultiSelectRoot>
  );
};

export default DialogSearchableMultiSelect;
